import React, { memo } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Chip,
  Typography,
  IconButton,
  Link,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { getPlatformStyles } from '../../../utils/getPlatformStyles';
import { getPlatformLogo } from '../../../utils/getPlatformLogo';
import { GameListEntry, PAUSABLE_STATES, STATE_LABELS } from '../types';
import { cardFoot, footPink, footSecondary } from '../../../utils/surfaces';
import { formatHltb, hltbUrl } from '../hltb';

interface GameListCardProps {
  game: GameListEntry;
  onMenuClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    game: GameListEntry
  ) => void;
  onTogglePaused: (game: GameListEntry) => void;
}

const formatFinishedDate = (game: GameListEntry) => {
  if (game.state !== 'finished' || !game.dateFinished) return '';

  return game.dateFinished.toDate().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

function GameListCard({ game, onMenuClick, onTogglePaused }: GameListCardProps) {
  const platform = getPlatformStyles(game.platform);
  const Logo = getPlatformLogo(game.platform);

  const canPause = PAUSABLE_STATES.includes(game.state);

  return (
    <Card
      sx={{
        borderRadius: 1.5,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.25,
          minHeight: 36,
          bgcolor: platform.bg,
          color: platform.color,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {Logo ? (
            <Logo
              style={{
                height: '36px',
                width: 'auto',
                display: 'block',
                color: game.platform === 'PS5' ? '#000000' : '#ffffff',
                flexShrink: 0,
                transform: 'translateY(1px)',
                transformOrigin: 'left center',
              }}
            />
          ) : (
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: platform.color, lineHeight: 1.1 }}
            >
              {platform.label}
            </Typography>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={(e) => onMenuClick(e, game)}
          sx={{ color: platform.color, p: 0.25 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <CardMedia
        component="img"
        src={game.image}
        alt={game.title}
        sx={{
          width: '100%',
          aspectRatio: '16 / 9',
          objectFit: 'cover',
        }}
      />

      <Box
        sx={(theme) => ({
          px: 1.5,
          py: 1,
          minHeight: 96,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 0.4,
          ...cardFoot(theme),
        })}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {game.title}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            minHeight: 28,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: footSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            {game.year ? <span>{game.year} ·</span> : null}
            <Link
              href={hltbUrl(game)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: footPink,
                textDecorationStyle: 'dotted',
                textUnderlineOffset: '3px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                whiteSpace: 'nowrap',
              }}
            >
              {formatHltb(game)}
            </Link>
          </Typography>

          {canPause && (
            <Tooltip title={game.paused ? 'Resume' : 'Mark as paused'}>
              <IconButton
                size="small"
                onClick={() => onTogglePaused(game)}
                sx={{
                  p: 0.25,
                  color: game.paused ? 'primary.main' : 'text.disabled',
                }}
              >
                {game.paused ? (
                  <PauseCircleIcon fontSize="small" />
                ) : (
                  <PauseCircleOutlineIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Chip
            size="small"
            label={
              canPause && game.paused
                ? 'Paused'
                : STATE_LABELS[game.state]
            }
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: (theme) =>
                theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
              bgcolor: (theme) =>
                canPause && game.paused
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.18)'
                    : 'rgba(80,60,140,0.12)'
                  : theme.palette.mode === 'dark'
                    ? 'rgba(255,47,146,0.35)'
                    : 'rgba(208,25,111,0.12)',
            }}
          />

          <Typography variant="caption" sx={{ color: footSecondary }}>
            {formatFinishedDate(game)}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

export default memo(GameListCard);
